import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { api } from "../services/api";

const AuthContext = createContext(null);

// Persists the user's active organization selection (see services/api.js —
// `request()` reads this same key directly to attach `X-Organization-Id`).
// A missing/empty value means "no header sent", i.e. the pre-existing
// single-org behavior (backend defaults to the caller's first membership).
const ACTIVE_ORG_STORAGE_KEY = "noesis_active_org_id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("noesis_user");
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [activeOrganizationId, setActiveOrganizationId] = useState(() => {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY) || null;
  });
  // Client commercial balance and rates used by generation previews.
  const [credits, setCredits] = useState(null); // balance summary
  const [creditRates, setCreditRates] = useState(null); // rate tables for local estimation

  const creditSequence = useRef(0);
  const catalogSequence = useRef(0);

  const refreshCredits = useCallback(async () => {
    if (!localStorage.getItem("noesis_token")) return null;
    const sequence = ++creditSequence.current;
    const organization = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    try {
      const balance = await api.getCreditBalance();
      if (
        sequence === creditSequence.current &&
        organization === localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
      )
        setCredits(balance);
      return balance;
    } catch {
      return null;
    }
  }, []);

  const refreshCreditCatalog = useCallback(async () => {
    if (!localStorage.getItem("noesis_token")) return null;
    const sequence = ++catalogSequence.current;
    const organization = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    try {
      const catalog = await api.getCreditCatalog();
      if (
        sequence === catalogSequence.current &&
        organization === localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
      ) {
        setCreditRates(catalog?.rates || null);
      }
      return catalog?.rates || null;
    } catch {
      return null;
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem("noesis_token");
    if (!token) return null;
    try {
      const userData = await api.getMe();
      localStorage.setItem("noesis_user", JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch {
      localStorage.removeItem("noesis_token");
      localStorage.removeItem("noesis_user");
      setUser(null);
      return null;
    }
  }, []);

  // Validate session on mount
  useEffect(() => {
    const token = localStorage.getItem("noesis_token");
    if (!token) {
      setLoading(false);
      return;
    }
    refreshMe().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)load the credit balance whenever the user or active org changes.
  useEffect(() => {
    setCreditRates(null);
    const selectedOrg = user?.organizations?.find(
      (org) => org.id === (activeOrganizationId || user.organizations?.[0]?.id),
    );
    if (
      !user?.is_agency_staff ||
      user.account_type === "client" ||
      !selectedOrg?.agency_client_id
    ) {
      creditSequence.current += 1;
      catalogSequence.current += 1;
      setCredits(null);
      return;
    }
    refreshCredits();
    refreshCreditCatalog();
    return () => {
      catalogSequence.current += 1;
    };
  }, [user, activeOrganizationId, refreshCredits, refreshCreditCatalog]);

  const login = useCallback(async (email, password) => {
    const data = await api.login({ email, password });
    localStorage.setItem("noesis_token", data.access_token);
    localStorage.setItem("noesis_user", JSON.stringify(data.user));
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    setActiveOrganizationId(null);
    setUser(data.user);
    return data.user;
  }, []);

  const portalLogin = useCallback(async (email, password) => {
    const data = await api.portalLogin({ email, password });
    localStorage.setItem("noesis_token", data.access_token);
    localStorage.setItem("noesis_user", JSON.stringify(data.user));
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    setActiveOrganizationId(null);
    setCredits(null);
    setCreditRates(null);
    setUser(data.user);
    return data.user;
  }, []);

  const acceptInvite = useCallback(async ({ token, name, password }) => {
    const data = await api.acceptInvite({ token, name, password });
    localStorage.setItem("noesis_token", data.access_token);
    localStorage.setItem("noesis_user", JSON.stringify(data.user));
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    setActiveOrganizationId(null);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("noesis_token");
    localStorage.removeItem("noesis_user");
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    setActiveOrganizationId(null);
    creditSequence.current += 1;
    catalogSequence.current += 1;
    setCredits(null);
    setCreditRates(null);
    setUser(null);
  }, []);

  // Switch the active organization. Passing the user's default (first) org id,
  // or null/undefined, clears the header entirely (pre-existing behavior).
  // Passing the literal "all" is only meaningful for superadmins on the
  // cross-org admin views — other pages simply won't send anything that
  // changes their own single-org results.
  const switchOrganization = useCallback(
    (orgId) => {
      const currentOrgId =
        localStorage.getItem(ACTIVE_ORG_STORAGE_KEY) ||
        user?.organizations?.[0]?.id;
      const targetOrgId = orgId || user?.organizations?.[0]?.id;
      if (currentOrgId === targetOrgId) return;
      creditSequence.current += 1;
      catalogSequence.current += 1;
      setCredits(null);
      setCreditRates(null);
      const defaultOrgId = user?.organizations?.[0]?.id || null;
      if (!orgId || orgId === defaultOrgId) {
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        setActiveOrganizationId(null);
      } else {
        localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
        setActiveOrganizationId(orgId);
      }
    },
    [user],
  );

  // The role that should gate admin-only UI for the CURRENTLY selected
  // organization (not just the user's first membership) — falls back to the
  // legacy top-level `role` field for older cached user payloads that predate
  // the `organizations[]` array.
  const activeOrgRole = useMemo(() => {
    if (!user) return null;
    if (activeOrganizationId === "all") return null; // handled via is_superadmin below
    const orgId = activeOrganizationId || user.organizations?.[0]?.id;
    const membership = (user.organizations || []).find((o) => o.id === orgId);
    return membership?.role || user.role;
  }, [user, activeOrganizationId]);

  // Canonical role + permission flags (single source of truth for role-based UI).
  // Product staff labels are Administrador and Especialista de marketing.
  const isSuperadmin = !!user?.is_superadmin;
  const canonicalRole = isSuperadmin ? "superadmin" : activeOrgRole || "member";
  const ROLE_LABELS = {
    superadmin: "Administrador",
    admin: "Administrador",
    member: "Especialista de marketing",
    agency_admin: "Administrador",
    agency_member: "Especialista de marketing",
    viewer: "Acceso limitado",
  };

  const activeOrganization =
    (user?.organizations || []).find(
      (org) =>
        org.id === (activeOrganizationId || user?.organizations?.[0]?.id),
    ) || null;
  const agencyOrganizationId =
    activeOrganization?.agency_organization_id ||
    (activeOrganization?.agency_client_id ? null : activeOrganization?.id) ||
    null;
  const agencyOrganization =
    (user?.organizations || []).find(
      (org) => org.id === agencyOrganizationId,
    ) || null;
  const isAgencyStaff =
    user?.account_type !== "client" && !!user?.is_agency_staff;
  const isAgencyAdmin =
    isAgencyStaff && (isSuperadmin || agencyOrganization?.role === "admin");

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: ["admin", "agency_admin"].includes(activeOrgRole) || isSuperadmin,
    isAgencyStaff,
    isAgencyAdmin,
    activeOrganization,
    agencyOrganization,
    agencyOrganizationId,
    isClientAccount: user?.account_type === "client",
    activeClientId: activeOrganization?.agency_client_id || null,
    isSuperadmin,
    // Approximate provider costs are visible only to the owning team administrator.
    canViewCosts: isAgencyAdmin,
    // A "pure" org admin (org admin who is not the global admin).
    isOrgAdmin:
      ["admin", "agency_admin"].includes(activeOrgRole) && !isSuperadmin,
    // Guest = authenticated but neither global nor org admin.
    isGuest:
      !!user &&
      !isSuperadmin &&
      !["admin", "agency_admin"].includes(activeOrgRole),
    role: canonicalRole,
    roleLabel:
      user?.account_type === "client"
        ? "Cliente"
        : ROLE_LABELS[canonicalRole] || canonicalRole,
    organizations: user?.organizations || [],
    activeOrganizationId,
    switchOrganization,
    refreshMe,
    credits,
    creditRates,
    refreshCredits,
    refreshCreditCatalog,
    login,
    portalLogin,
    acceptInvite,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
